import {
  type DefaultError,
  type InfiniteData,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { queryKey } from '../../../constant/queryKey';
import { type MarketHomeGoodsItem, marketService } from 'shared/src/api';
import type { MarketGoodsListFilter } from '../states/GoodsListFilter.atom';
import { convertMarketHomeGoodsItemToGoodsData } from '../utils/convertMarketHomeGoodsItemToGoodsData';
import type { GoodsData } from '@banhala/ui-kit';
import type {
  GetMarketParentHomeGoodsRequestParams,
  GetMarketParentHomeGoodsResponse,
} from 'shared/src/api/marketService/getMarketParentHomeGoods/types';
import type { InfiniteQueryHookOptions } from 'shared/src/types';
import { isNil } from 'shared/src/utils';

export const useParentMarketHomeGoodsQuery = (
  params: {
    marketId: number;
    filters: MarketGoodsListFilter;
  },
  options: InfiniteQueryHookOptions<
    GetMarketParentHomeGoodsResponse,
    DefaultError,
    InfiniteData<
      GetMarketParentHomeGoodsResponse & {
        goods: (GoodsData & MarketHomeGoodsItem)[];
      }
    >,
    (string | number | MarketGoodsListFilter)[],
    string | null | undefined
  >,
) => {
  const { marketId, filters } = params;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    refetch,
    isRefetching,
    isFetchingNextPage,
    status,
    isLoading,
  } = useInfiniteQuery<
    GetMarketParentHomeGoodsResponse,
    DefaultError,
    InfiniteData<
      GetMarketParentHomeGoodsResponse & {
        goods: (GoodsData & MarketHomeGoodsItem)[];
      }
    >,
    (string | number | MarketGoodsListFilter)[],
    string | null | undefined
  >({
    queryKey: queryKey.marketParentGoodsList(marketId, filters),
    queryFn: ({ pageParam }: { pageParam?: string | null }) => {
      const convertToRequestParams: () => GetMarketParentHomeGoodsRequestParams =
        () => {
          return {
            category_sno:
              filters.subCategorySno ?? filters.categorySno ?? undefined,
            order: filters.sort,
            next_token: pageParam ?? undefined,
            delivery_type: filters.deliveryType ?? undefined,
            query: filters.keyword === '' ? undefined : filters.keyword,
          };
        };
      return marketService.getMarketParentHomeGoods(
        marketId,
        convertToRequestParams(),
      );
    },
    initialPageParam: undefined,
    select: (data: InfiniteData<GetMarketParentHomeGoodsResponse>) => {
      return {
        ...data,
        pages: data.pages.map(page => ({
          ...page,
          goods: page.components
            .map(component =>
              component.entity.item_list.map(
                convertMarketHomeGoodsItemToGoodsData,
              ),
            )
            .flat(1),
        })),
      };
    },
    getNextPageParam: ({ next_token }: { next_token: string | null }) => {
      if (isNil(next_token)) {
        return;
      }
      // const lastGoodsSno = data.goods[lastIdx].sno;
      // return lastGoodsSno;
      return next_token;
    },
    staleTime: 1000 * 10, // 10 seconds
    gcTime: 1000 * 10, // 10 seconds
    ...options,
  });

  return {
    marketHomeGoods: data?.pages.map(item => item.goods).flat() ?? [],
    hasTodayGoods:
      data?.pages.map(item => item.has_today_goods).some(Boolean) ?? false,
    hasShakGoods:
      data?.pages.map(item => item.has_shak_goods).some(Boolean) ?? false,
    totalCount: Math.max(...(data?.pages?.map(item => item.total_count) ?? [])),
    listId: data?.pages?.[0]?.list_id ?? null,
    refetch,
    hasNextPage,
    isRefetching,
    status,
    getNextMarketHomeGoods: () => {
      if (!hasNextPage) return;

      if (isFetchingNextPage) return;

      fetchNextPage();
    },
    isLoading,
  };
};
